/**
 * OPS-004B1 · Canonical stage frontend helpers.
 *
 * The backend `core.lifecycle.STAGE_CATALOGUE` is the SOLE source of
 * stage keys, labels, ownership and SLA policy. This module is
 * intentionally trimmed to frontend-only concerns:
 *
 *   * SLA + owner display maps (tones/colours are UI decisions).
 *   * `formatTimeInStage` — pure formatting helper.
 *   * `buildStageFilterOptions(catalogue)` — turns the backend
 *     catalogue response into an <option> list for a <select>.
 *   * `dashboardDeepLink()` — canonical URL builder for every
 *     dashboard tile.
 *
 * No lifecycle progression list. No expected-owner rules. No SLA
 * arithmetic. `stage_label` on every serialized project row comes
 * pre-hydrated from the backend — components read that field and
 * fall back to the raw `current_stage` key only when the row is
 * missing (never their own hardcoded map).
 */

// ── Display maps (tone + role labels are pure UI) ───────────────

export const SLA_STATUS_LABELS = {
    on_track:       { label: "On track",       tone: "green" },
    due_soon:       { label: "Due soon",       tone: "amber" },
    overdue:        { label: "Overdue",        tone: "red" },
    not_applicable: { label: "Not applicable", tone: "neutral" },
};

export const SLA_FILTER_OPTIONS = [
    ["", "Any SLA state"],
    ["on_track", "On track"],
    ["due_soon", "Due soon"],
    ["overdue",  "Overdue"],
    ["not_applicable", "Not applicable"],
];

export const OWNER_ROLE_LABELS = {
    operations: "Operations",
    engineer:   "Engineer",
    installer:  "Installer",
    sales:      "Sales",
    customer:   "Customer",
    system:     "System",
};

// ── Formatting helpers ─────────────────────────────────────────

/**
 * Format the `time_in_stage` blob (`{ hours, days, seconds }`)
 * hydrated by `hydrate_stage_view` into a compact human string.
 */
export function formatTimeInStage(tis) {
    if (!tis || typeof tis !== "object") return "—";
    const { hours = 0, days = 0 } = tis;
    if (days >= 1) {
        const d = Math.floor(days);
        return d === 1 ? "1 day" : `${d} days`;
    }
    const h = Math.max(0, Math.floor(hours));
    if (h >= 1) return h === 1 ? "1 hr" : `${h} hrs`;
    return "< 1 hr";
}

// ── URL filter registry ────────────────────────────────────────

export const OPS004B_FILTER_KEYS = [
    "current_stage",
    "stage_sla_status",
    "stalled_only",
];

// ── Catalogue-driven filter options ────────────────────────────

/**
 * Turn the `/api/operations/stages/catalogue` response into the
 * option-tuple list `<AdvFilter>` expects.
 *
 * Empty / missing catalogue → the "Any stage" placeholder only,
 * so a partial network state degrades gracefully without leaking
 * a client-side default progression.
 */
export function buildStageFilterOptions(catalogue) {
    const stages = (catalogue && catalogue.stages) || [];
    return [
        ["", "Any stage"],
        ...stages.map((s) => [s.key, s.label]),
    ];
}

/**
 * Map `{ key → label }` derived from the catalogue. Consumers use
 * this ONLY when a serialized project row is missing the pre-
 * hydrated `stage_label` field (legacy/edge case).
 */
export function stageLabelMap(catalogue) {
    const stages = (catalogue && catalogue.stages) || [];
    const out = {};
    for (const s of stages) out[s.key] = s.label;
    return out;
}

// ── Deep-link contract ─────────────────────────────────────────

/**
 * Return the `/operations/...` URL that filters to the matching
 * subset. `kind` values map 1:1 to the dashboard tiles. Notification
 * URLs match the Notification Centre canonical contract
 * (`tab=deliveries` + `status=failed | retryable=true` + `page=1`).
 */
export function dashboardDeepLink(kind, args = {}) {
    switch (kind) {
        case "active_projects":
            return "/operations/projects?state=active";
        case "recently_completed":
            return "/operations/projects?state=completed";
        case "unassigned_projects":
            return "/operations/projects?assigned=false&state=active";
        case "overdue_stages":
            return "/operations/projects?stage_sla_status=overdue&focusStage=1";
        case "stalled_stages":
            return "/operations/projects?stalled_only=true&focusStage=1";
        case "projects_by_stage":
            return `/operations/projects?current_stage=${encodeURIComponent(args.stage || "")}`;
        // OPS-DASHBOARD-DEEP-LINKS · The `auto_open=first` marker is a
        // one-shot hint consumed by OperationsAttentionCentre — it
        // resolves to the first deterministic matching active
        // exception (list is sorted severity_desc + age_desc by the
        // server) and is stripped from the URL as soon as a concrete
        // `open=<exception_id>` is written. An explicit `open=` on
        // the URL always wins.
        case "critical_attention":
            return "/operations/attention?severity=critical&auto_open=first";
        case "high_attention":
            return "/operations/attention?severity=high&auto_open=first";
        case "surveys_scheduling":
            return "/operations/projects?queue=survey_required";
        case "surveys_review":
            return "/operations/projects?queue=survey_submitted";
        case "surveys_clarification":
            return "/operations/projects?queue=survey_clarification";
        case "upcoming_surveys":
            return "/operations/projects?queue=survey_scheduled";
        case "upcoming_installations":
            return "/operations/projects?queue=installation_this_week";
        case "logistics_outstanding":
            return "/operations/projects?queue=logistics_outstanding";
        case "notifications_failing":
            return "/operations/notifications?tab=deliveries&status=failed&page=1";
        case "notifications_retryable":
            return "/operations/notifications?tab=deliveries&retryable=true&page=1";
        case "notifications_centre":
            return "/operations/notifications?tab=deliveries&page=1";
        case "engineer_workload":
            return "/operations/engineers";
        default:
            return "/operations/projects";
    }
}
